/**
 * 📋 Enterprise Action Service
 *
 * Supabase CRUD for the `actions` table.
 * Actions are organizational tasks linked to projects, issues, and workflows.
 *
 * Rules:
 *  - Always filter by organization_id
 *  - Never call Supabase directly in components
 *  - Respect RLS and multi-tenancy
 */

import { supabase } from '../lib/supabaseClient';
import { isValidUuid } from '../utils/uuid';
import { applyScopeFilter, type DataScope } from './scopeService';

// ─── Types ──────────────────────────────────────────────────

export type ActionStatus = 'pending' | 'in_progress' | 'completed' | 'blocked' | 'cancelled';
export type ActionPriority = 'low' | 'medium' | 'high' | 'urgent';
export type ActionSlaStatus = 'on_time' | 'andon_triggered' | 'critical_director' | 'overdue';

export interface EnterpriseAction {
  id: string;
  organization_id: string;
  project_id: string | null;
  title: string;
  description: string | null;
  status: ActionStatus;
  priority: ActionPriority;
  due_date: string | null;
  completed_at: string | null;
  assigned_to: string | null;
  created_by: string | null;
  owner_id: string | null;
  effort: number | null;
  impact: number | null;
  plan_id: string | null;
  /** Contramedida direta do problema. Ação de oportunidade fica sem este vínculo. */
  problem_id?: string | null;
  metadata: { team_members?: string[]; [key: string]: unknown } | null;
  created_at: string;
  updated_at: string;
  // Joined
  project_name?: string | null;
  assigned_name?: string | null;
  // SLA (computed column populated by Supabase trigger/function)
  sla_status?: ActionSlaStatus | null;
  hoshin_goal_id?: string | null;
}

export interface ActionFilters {
  status?: ActionStatus;
  priority?: ActionPriority;
  project_id?: string;
  assigned_to?: string;
  search?: string;
  overdue_only?: boolean;
  plan_id?: string;
  problem_id?: string;
}

export const ACTION_STATUS_LABELS: Record<ActionStatus, string> = {
  pending: 'Pendente',
  in_progress: 'Em Execução',
  completed: 'Concluída',
  blocked: 'Bloqueada',
  cancelled: 'Cancelada',
};

export const ACTION_STATUS_COLORS: Record<ActionStatus, string> = {
  pending: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  blocked: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  cancelled: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500',
};

export const ACTION_PRIORITY_LABELS: Record<ActionPriority, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente',
};

export const ACTION_PRIORITY_COLORS: Record<ActionPriority, string> = {
  low: 'text-slate-500',
  medium: 'text-blue-500',
  high: 'text-orange-500',
  urgent: 'text-red-500',
};

/** Sem responsável: depois deste prazo a ação passa para quem criou. */
export const ACTION_ORPHAN_GRACE_HOURS = 24;

// ─── Service ────────────────────────────────────────────────

export class ActionService {
  /**
   * Ações ainda sem responsável, criadas há mais de ACTION_ORPHAN_GRACE_HOURS,
   * passam a ser de quem criou. Não mexe em ação recém-aberta.
   */
  static async promoteOrphansToCreator(
    organizationId: string,
    userId: string,
    graceHours: number = ACTION_ORPHAN_GRACE_HOURS,
  ): Promise<void> {
    if (!organizationId || !userId) return;
    const cutoff = new Date(Date.now() - graceHours * 60 * 60 * 1000).toISOString();
    const { error } = await supabase
      .from('actions')
      .update({ assigned_to: userId, updated_at: new Date().toISOString() })
      .eq('organization_id', organizationId)
      .eq('created_by', userId)
      .is('assigned_to', null)
      .lte('created_at', cutoff)
      .not('status', 'in', '(completed,cancelled)');
    if (error && import.meta.env.DEV) {
      console.warn('[ActionService.promoteOrphansToCreator]', error.message);
    }
  }

  /**
   * List actions for an organization.
   * Optionally applies hierarchical scope filter (lead/manager/director).
   */
  static async list(
    organizationId: string,
    filters?: ActionFilters,
    scope?: DataScope | null,
  ): Promise<EnterpriseAction[]> {
    let query = supabase
      .from('actions')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.priority) query = query.eq('priority', filters.priority);
    if (filters?.project_id) {
      if (isValidUuid(filters.project_id)) {
        query = query.eq('project_id', filters.project_id);
      } else {
        console.warn('[ActionService.list] Ignorando project_id inválido:', filters.project_id);
      }
    }
    if (filters?.assigned_to) query = query.eq('assigned_to', filters.assigned_to);
    if (filters?.search) query = query.ilike('title', `%${filters.search}%`);
    if (filters?.plan_id) query = query.eq('plan_id', filters.plan_id);
    if (filters?.problem_id && isValidUuid(filters.problem_id)) {
      query = query.or(`problem_id.eq.${filters.problem_id},metadata->>problem_id.eq.${filters.problem_id}`);
    }
    if (filters?.overdue_only) {
      const today = new Date().toISOString().slice(0, 10);
      query = query
        .not('status', 'in', '(completed,cancelled)')
        .lt('due_date', today);
    }

    // Hierarchical scope (skip when caller passes an explicit assignee filter)
    if (scope && !filters?.assigned_to) {
      query = applyScopeFilter(query, scope, 'assigned_to', 'department_id');
    }

    const { data, error } = await query;
    if (error) {
      console.warn('[ActionService.list] Query failed:', error.message);
      return [];
    }

    // Enrich with project names
    const actions = (data ?? []) as EnterpriseAction[];
    const projectIds = [...new Set(actions.map(a => a.project_id).filter(Boolean))];
    if (projectIds.length > 0) {
      const { data: projects } = await supabase
        .from('projects')
        .select('id, name')
        .in('id', projectIds);
      if (projects) {
        const map = Object.fromEntries(projects.map(p => [p.id, p.name]));
        for (const a of actions) {
          if (a.project_id) a.project_name = map[a.project_id] ?? null;
        }
      }
    }

    // Enrich with assigned user names
    const userIds = [...new Set(actions.map(a => a.assigned_to).filter(Boolean))] as string[];
    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('user_id, name')
        .in('user_id', userIds);
      if (profiles) {
        const map = Object.fromEntries(profiles.map(p => [p.user_id, p.name]));
        for (const a of actions) {
          if (a.assigned_to) a.assigned_name = map[a.assigned_to] ?? null;
        }
      }
    }

    return actions;
  }

  /**
   * Get a single action by ID.
   */
  static async getById(actionId: string): Promise<EnterpriseAction | null> {
    const { data, error } = await supabase
      .from('actions')
      .select('*')
      .eq('id', actionId)
      .single();
    if (error) return null;
    return data as EnterpriseAction;
  }

  /**
   * Create a new action.
   */
  static async create(action: {
    organization_id: string;
    project_id?: string;
    plan_id?: string;
    problem_id?: string;
    title: string;
    description?: string;
    status?: ActionStatus;
    priority?: ActionPriority;
    due_date?: string;
    assigned_to?: string;
    created_by?: string;
    owner_id?: string;
    effort?: number;
    impact?: number;
    metadata?: Record<string, unknown>;
    team_members?: string[];
  }): Promise<EnterpriseAction | null> {
    if (!action.created_by) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) action = { ...action, created_by: session.user.id };
    }
    const { team_members, metadata, problem_id, ...rest } = action;
    const mergedMetadata = {
      ...(metadata ?? {}),
      ...(problem_id ? { problem_id } : {}),
      ...(team_members && team_members.length > 0 ? { team_members } : {}),
    };
    const fullPayload = {
      ...rest,
      ...(problem_id ? { problem_id } : {}),
      status: action.status ?? 'pending',
      priority: action.priority ?? 'medium',
      ...(Object.keys(mergedMetadata).length > 0 ? { metadata: mergedMetadata } : {}),
    };

    const { data, error } = await supabase
      .from('actions')
      .insert(fullPayload)
      .select()
      .single();
    if (error) {
      const schemaColumnMismatch = error.code === 'PGRST204' || /column|schema cache|Could not find/i.test(error.message);
      if (!schemaColumnMismatch) {
        console.error('[ActionService.create] Failed:', error.message);
        return null;
      }

      const compatibilityPayload = {
        organization_id: action.organization_id,
        project_id: action.project_id,
        title: action.title,
        description: action.description ?? null,
        status: action.status ?? 'pending',
        priority: action.priority ?? 'medium',
        due_date: action.due_date,
        assigned_to: action.assigned_to,
        created_by: action.created_by,
        ...(Object.keys(mergedMetadata).length > 0 ? { metadata: mergedMetadata } : {}),
      };
      const { data: compatibilityData, error: compatibilityError } = await supabase
        .from('actions')
        .insert(compatibilityPayload)
        .select()
        .single();

      if (!compatibilityError) return compatibilityData as EnterpriseAction;

      const minimalPayload = {
        organization_id: action.organization_id,
        project_id: action.project_id,
        title: action.title,
        description: action.description ?? null,
        status: action.status ?? 'pending',
        priority: action.priority ?? 'medium',
        assigned_to: action.assigned_to,
        created_by: action.created_by,
      };
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('actions')
        .insert(minimalPayload)
        .select()
        .single();

      if (fallbackError) {
        console.error('[ActionService.create] Failed:', fallbackError.message);
        return null;
      }
      return fallbackData as EnterpriseAction;
    }
    return data as EnterpriseAction;
  }

  /**
   * Update an action.
   */
  static async update(actionId: string, updates: Partial<{
    title: string;
    description: string;
    status: ActionStatus;
    priority: ActionPriority;
    due_date: string | null;
    assigned_to: string | null;
    completed_at: string | null;
    effort: number | null;
    impact: number | null;
    metadata: EnterpriseAction['metadata'];
  }>): Promise<EnterpriseAction | null> {
    // Auto-set completed_at when completing
    if (updates.status === 'completed' && !updates.completed_at) {
      updates.completed_at = new Date().toISOString();
    }
    // Clear completed_at if reopening
    if (updates.status && updates.status !== 'completed') {
      updates.completed_at = null;
    }

    const { data, error } = await supabase
      .from('actions')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', actionId)
      .select()
      .single();
    if (error) {
      console.error('[ActionService.update] Failed:', error.message);
      return null;
    }
    const updated = data as EnterpriseAction;
    if (updates.status === 'completed' && updated.organization_id) {
      void import('./gamificationService').then(({ GamificationService }) =>
        GamificationService.awardActivity({
          organizationId: updated.organization_id,
          kind: 'action',
          sourceId: updated.id,
          label: updated.title,
        }),
      );
    }
    return updated;
  }

  /**
   * Delete an action.
   */
  static async delete(actionId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('actions')
      .delete()
      .eq('id', actionId)
      .select('id');
    if (error) {
      console.error('[ActionService.delete] Failed:', error.message);
      return false;
    }
    // RLS bloqueia DELETE sem erro: 0 linhas afetadas.
    if (!data || data.length === 0) {
      console.warn('[ActionService.delete] Nenhuma linha removida (sem permissão?):', actionId);
      return false;
    }
    return true;
  }

  /**
   * Get action counts by status for dashboard.
   */
  static async getStatusCounts(organizationId: string): Promise<Record<ActionStatus, number>> {
    const { data, error } = await supabase
      .from('actions')
      .select('status')
      .eq('organization_id', organizationId);

    const counts: Record<ActionStatus, number> = {
      pending: 0, in_progress: 0, completed: 0, blocked: 0, cancelled: 0,
    };
    if (error || !data) return counts;
    for (const row of data) {
      const s = row.status as ActionStatus;
      if (s in counts) counts[s]++;
    }
    return counts;
  }

  /**
   * Count overdue actions.
   */
  static async countOverdue(organizationId: string): Promise<number> {
    const today = new Date().toISOString().slice(0, 10);
    const { data } = await supabase
      .from('actions')
      .select('id')
      .eq('organization_id', organizationId)
      .not('status', 'in', '(completed,cancelled)')
      .not('due_date', 'is', null)
      .lt('due_date', today);
    return data?.length ?? 0;
  }
}
